import { useEffect, useState } from 'react';
import { NanoCheckoutOfferUsedStage } from '../analytics/events';
import { hasRedeemedGwaOffer } from './gwaRedeemed';
import { CheckoutOffer } from './offer';

interface GwaOfferUsedState {
  /** True once the check has answered, or had nothing to ask, so the checkout can render once. */
  settled: boolean;
  usedAt: NanoCheckoutOfferUsedStage | null;
}

/**
 * Whether the adoption offer on this checkout has already been used by the shopper's account.
 *
 * `accountEmail` is the Fi account's, not the contact email registration collected: web limits the offer
 * per account, and the account is what the purchase is made against.
 *
 * Once the offer is known to be used it stays used. A later answer of "not used" cannot bring the form
 * back, because the purchase it would let through is the one web has already rejected.
 *
 * @param accountResolved false while the account is still loading; the check waits for it
 */
export function useGwaOfferUsed(offer: CheckoutOffer | null, accountEmail: string | null, accountResolved: boolean) {
  const [state, setState] = useState<GwaOfferUsedState>({ settled: false, usedAt: null });

  useEffect(() => {
    if (!offer || !accountResolved) {
      return;
    }

    let cancelled = false;
    void (async () => {
      const used = accountEmail ? await hasRedeemedGwaOffer(accountEmail, offer) : false;
      if (!cancelled) {
        setState((current) => ({ settled: true, usedAt: current.usedAt ?? (used ? 'precheck' : null) }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [offer, accountEmail, accountResolved]);

  return {
    ...state,
    /** The purchase itself was rejected as already used, which the check did not catch. */
    markUsedAtPurchase: () => setState({ settled: true, usedAt: 'purchase' }),
  };
}
